import AppEvents from 'lib/AppEvents';
import EventService from '../services/EventService';
import { DATE_FORMAT, FULL_DATE_FORMAT, HOURLY_FORMAT, last3, last30, last7, lastMonth, previous7, previousMonthLastYear, thisMonth, thisMonthLastYear, today, twoMonths, yesterday } from '../utils/DateUtils';

// THIS FILE SHOULD NOT:
// use moment() at all, or declare any of its own relative dates (put it in DateUtils and normalize to timezone instead).

/**
 * @description Creates an arbitrary key according to the current hour. This allows it be used to manage data cached by hour.
 * @param {string} prefix
 * @return {string}
 */
export const getHourlyCacheKey = (prefix) => {
    return `${prefix}_${today().format(DATE_FORMAT + HOURLY_FORMAT)}`;
}

export const makeCacheKey = (prefix, dateFrom, dateTo?) => {
    let k = `${prefix}`;
    if (dateFrom) {
        k += `_${dateFrom.format(FULL_DATE_FORMAT)}`;
    }
    if (dateTo) {
        k += `_${dateTo.format(FULL_DATE_FORMAT)}`;
    }
    return k;
}

// Account-data keys: ----------------------------------------

export const account_data_today_key = () => {
    return 'account-data-today-' + today().format(DATE_FORMAT);
}

export const account_data_today_hourly_key = () => {
    return 'account-data-today-' + today().format(DATE_FORMAT + HOURLY_FORMAT);
}

export const account_data_yesterday_key = () => {
    return 'account-data-yesterday-' + yesterday().format(DATE_FORMAT);
}

export const account_data_previous_7_key = () => {
    let _previous7 = previous7();
    let start = _previous7[0].format(DATE_FORMAT);
    let end = _previous7[1].format(DATE_FORMAT);
    return 'account-data-previous-7-' + start + '-to-' + end;
}

export const account_data_last_3_key = () => {
    let _last3 = last3();
    let start = _last3[0].format(DATE_FORMAT);
    let end = _last3[1].format(DATE_FORMAT);
    return 'account-data-last_3-' + start + '-to-' + end;
}

export const account_data_last_7_key = () => {
    let _last7 = last7();
    let start = _last7[0].format(DATE_FORMAT);
    let end = _last7[1].format(DATE_FORMAT);
    return 'account-data-last_7-' + start + '-to-' + end;
}

export const account_data_last_30_key = () => {
    let _last30 = last30();
    let start = _last30[0].format(DATE_FORMAT);
    let end = _last30[1].format(DATE_FORMAT);
    return 'account-data-last_30-' + start + '-to-' + end;
}

export const account_data_this_month_key = () => {
    let _thisMonth = thisMonth();
    let start = _thisMonth[0].format(DATE_FORMAT);
    let end = _thisMonth[1].format(DATE_FORMAT);
    return 'account-data-this-month-' + start + '-to-' + end;
}

export const account_data_last_month_key = () => {
    let _lastMonth = lastMonth();
    let start = _lastMonth[0].format(DATE_FORMAT);
    let end = _lastMonth[1].format(DATE_FORMAT);
    return 'account-data-last-month-' + start + '-to-' + end;
}

export const account_data_two_months_key = () => {
    let _twoMonths = twoMonths();
    let start = _twoMonths[0].format(DATE_FORMAT);
    let end = _twoMonths[1].format(DATE_FORMAT);
    return 'account-data-two-months-' + start + '-to-' + end;
}

export const account_data_this_month_last_year_key = () => {
    let _thisMonthLastYear = thisMonthLastYear();
    let start = _thisMonthLastYear[0].format(DATE_FORMAT);
    let end = _thisMonthLastYear[1].format(DATE_FORMAT);
    return 'account-data-this-month-last-year-' + start + '-to-' + end;
}

export const account_data_previous_month_last_year_key = () => {
    let _previousMonthLastYear = previousMonthLastYear();
    let start = _previousMonthLastYear[0].format(DATE_FORMAT);
    let end = _previousMonthLastYear[1].format(DATE_FORMAT);
    return 'account-data-previous-month-last-year-' + start + '-to-' + end;
}

export const account_purchase_events_today_hourly_key = () => {
    return 'account-purchase-events-today-' + today().format(DATE_FORMAT + HOURLY_FORMAT);
}

export const account_purchase_events_yesterday_key = () => {
    return 'account-purchase-events-yesterday-' + yesterday().format(DATE_FORMAT);
}

export const account_purchase_events_last_7_key = () => {
    let _last7 = last7();
    let start = _last7[0].format(DATE_FORMAT);
    let end = _last7[1].format(DATE_FORMAT);
    return 'account-purchase-events-last-7-' + start + '-to-' + end;
}

export const account_campaign_stats_today_hourly_key = () => {
    return 'account-campaign-stats-today-' + today().format(DATE_FORMAT + HOURLY_FORMAT);
}

export const account_campaign_stats_yesterday_key = () => {
    return 'account-campaign-stats-yesterday-' + yesterday().format(DATE_FORMAT);
}

export const account_campaign_stats_last_7_key = () => {
    let _last7 = last7();
    let start = _last7[0].format(DATE_FORMAT);
    let end = _last7[1].format(DATE_FORMAT);
    return 'account-campaign-stats-last-7-' + start + '-to-' + end;
}



// FB-specific: -------------------------------------------

export const fb_adsets_today_key = () => {
    return 'fb-adsets-today-' + today().format(DATE_FORMAT);
}

export const fb_adsets_today_hourly_key = () => {
    return 'fb-adsets-today-' + today().format(DATE_FORMAT + HOURLY_FORMAT);
}

export const fb_adsets_yesterday_key = () => {
    return 'fb-adsets-yesterday-' + yesterday().format(DATE_FORMAT);
}

export const fb_adsets_last_7_key = () => {
    let _last7 = last7();
    let start = _last7[0].format(DATE_FORMAT);
    let end = _last7[1].format(DATE_FORMAT);
    return 'fb-adsets-last-7-' + start + '-to-' + end;
}


export const fb_campaigns_today_key = () => {
    return 'fb-campaigns-today-' + today().format(DATE_FORMAT);
}

export const fb_campaigns_today_hourly_key = () => {
    return 'fb-campaigns-today-' + today().format(DATE_FORMAT + HOURLY_FORMAT);
}

export const fb_campaigns_yesterday_key = () => {
    return 'fb-campaigns-yesterday-' + yesterday().format(DATE_FORMAT);
}

export const fb_campaign_list_hourly_key = () => {
    return 'fb-campaign-list-' + today().format(DATE_FORMAT + HOURLY_FORMAT);
}

export const fb_campaigns_last_7_key = () => {
    let _last7 = last7();
    let start = _last7[0].format(DATE_FORMAT);
    let end = _last7[1].format(DATE_FORMAT);
    return 'fb-campaigns-last_7-' + start + '-to-' + end;
}

export const fb_campaigns_last_7_active_key = () => {
    let _last7 = last7();
    let start = _last7[0].format(DATE_FORMAT);
    let end = _last7[1].format(DATE_FORMAT);
    return 'fb-campaigns-last-7-active-' + start + '-to-' + end;
}


export const fb_insights_previous_7_key = () => {
    let _previous7 = previous7();
    let start = _previous7[0].format(DATE_FORMAT);
    let end = _previous7[1].format(DATE_FORMAT);
    return 'fb-insights-previous-7-' + start + '-to-' + end;
}

export const fb_insights_last_3_key = () => {
    let _last3 = last3();
    let start = _last3[0].format(DATE_FORMAT);
    let end = _last3[1].format(DATE_FORMAT);
    return 'fb-insights-last-3-' + start + '-to-' + end;
}

export const fb_insights_last_7_key = () => {
    let _last7 = last7();
    let start = _last7[0].format(DATE_FORMAT);
    let end = _last7[1].format(DATE_FORMAT);
    return 'fb-insights-last-7-' + start + '-to-' + end;
}

export const fb_insights_this_month_key = () => {
    let _thisMonth = thisMonth();
    let start = _thisMonth[0].format(DATE_FORMAT);
    let end = _thisMonth[1].format(DATE_FORMAT);
    return 'fb-insights-this-month-' + start + '-to-' + end;
}

export const fb_insights_last_month_key = () => {
    let _lastMonth = lastMonth();
    let start = _lastMonth[0].format(DATE_FORMAT);
    let end = _lastMonth[1].format(DATE_FORMAT);
    return 'fb-insights-last-month-' + start + '-to-' + end;
}

export const fb_insights_two_months_key = () => {
    let _twoMonths = twoMonths();
    let start = _twoMonths[0].format(DATE_FORMAT);
    let end = _twoMonths[1].format(DATE_FORMAT);
    return 'fb-insights-two-months-' + start + '-to-' + end;
}

export const fb_insights_this_month_last_year_key = () => {
    let _thisMonthLastYear = thisMonthLastYear();
    let start = _thisMonthLastYear[0].format(DATE_FORMAT);
    let end = _thisMonthLastYear[1].format(DATE_FORMAT);
    return 'fb-insights-this-month-last-year-' + start + '-to-' + end;
}

export const fb_insights_previous_month_last_year_key = () => {
    let _previousMonthLastYear = previousMonthLastYear();
    let start = _previousMonthLastYear[0].format(DATE_FORMAT);
    let end = _previousMonthLastYear[1].format(DATE_FORMAT);
    return 'fb-insights-previous-month-last-year-' + start + '-to-' + end;
}

// --------------------------------------------------------------------------------
// GoogleAds-specific: -------------------------------------------
export const googleAds_campaigns_today_key = () => {
    return 'googleAds-campaigns-today-' + today().format(DATE_FORMAT);
}

export const googleAds_campaigns_yesterday_key = () => {
    return 'googleAds-campaigns-yesterday-' + yesterday().format(DATE_FORMAT);
}

export const googleAds_campaign_previous_7_key = () => {
    let _last7 = last7();
    let start = _last7[0].format(DATE_FORMAT);
    let end = _last7[1].format(DATE_FORMAT);
    return 'googleAds-campaign-last-7-' + start + '-to-' + end;
}

// --------------------------------------------------------------------------------

// this holds the global "current" values. It is recalculated every page refresh,
// and app data 'Refresh' to ensure date range keys are accurate for the current time period.
// The keys are manually recalculated during this time with a call to recalculate_keys() below.
let currentDates: any = {};

export const recalculate_cache_key_dates = () => {
    currentDates.fb_insights_previous_7 = fb_insights_previous_7_key();
    currentDates.fb_insights_last_3 = fb_insights_last_3_key();
    currentDates.fb_insights_last_7 = fb_insights_last_7_key();
    currentDates.fb_insights_this_month = fb_insights_this_month_key();
    currentDates.fb_insights_last_month = fb_insights_last_month_key();
    currentDates.fb_insights_two_months = fb_insights_two_months_key();
    currentDates.fb_insights_this_month_last_year = fb_insights_this_month_last_year_key();
    currentDates.fb_insights_previous_month_last_year = fb_insights_previous_month_last_year_key();
    currentDates.fb_adsets_today = fb_adsets_today_key();
    currentDates.fb_adsets_today_hourly = fb_adsets_today_hourly_key();
    currentDates.fb_adsets_yesterday = fb_adsets_yesterday_key();
    currentDates.fb_adsets_last_7 = fb_adsets_last_7_key();
    currentDates.fb_campaigns_last_7 = fb_campaigns_last_7_key();
    currentDates.fb_campaigns_last_7_active = fb_campaigns_last_7_active_key();
    currentDates.fb_campaigns_today = fb_campaigns_today_key();
    currentDates.fb_campaigns_today_hourly = fb_campaigns_today_hourly_key();
    currentDates.fb_campaigns_yesterday = fb_campaigns_yesterday_key();
    currentDates.fb_campaign_list_hourly = fb_campaign_list_hourly_key();

    currentDates.account_data_today = account_data_today_key();
    currentDates.account_data_today_hourly = account_data_today_hourly_key();
    currentDates.account_data_yesterday = account_data_yesterday_key();
    currentDates.account_data_last_3 = account_data_last_3_key();
    currentDates.account_data_last_7 = account_data_last_7_key();
    currentDates.account_data_last_30 = account_data_last_30_key();
    currentDates.account_data_previous_7 = account_data_previous_7_key();

    currentDates.account_data_this_month = account_data_this_month_key();
    currentDates.account_data_last_month = account_data_last_month_key();
    currentDates.account_data_two_months = account_data_two_months_key();
    currentDates.account_data_previous_month_last_year = account_data_previous_month_last_year_key();
    currentDates.account_data_this_month_last_year = account_data_this_month_last_year_key();

    currentDates.account_purchase_events_today_hourly = account_purchase_events_today_hourly_key();
    currentDates.account_purchase_events_yesterday = account_purchase_events_yesterday_key();
    currentDates.account_purchase_events_last_7 = account_purchase_events_last_7_key();

    currentDates.account_campaign_stats_today_hourly = account_campaign_stats_today_hourly_key();
    currentDates.account_campaign_stats_yesterday = account_campaign_stats_yesterday_key();
    currentDates.account_campaign_stats_last_7 = account_campaign_stats_last_7_key();

    //console.log('⏰ New cache date keys calculated: ', currentDates);

    EventService.dispatch(AppEvents.DATE_KEYS_CHANGED, currentDates);

}
window.recalculate_cache_key_dates = recalculate_cache_key_dates;

// generate initial dates
// can't do this here yet... import is causing it to run before calculate_core_dates();
//recalculate_cache_key_dates();

// These are the keys that should be globally used:
export const account_data_today = () => currentDates.account_data_today;
export const account_data_today_hourly = () => currentDates.account_data_today_hourly;
export const account_data_yesterday = () => currentDates.account_data_yesterday;
export const account_data_previous_7 = () => currentDates.account_data_previous_7;
export const account_data_last_3 = () => currentDates.account_data_last_3;
export const account_data_last_7 = () => currentDates.account_data_last_7;
export const account_data_last_30 = () => currentDates.account_data_last_30;
export const account_data_last_month = () => currentDates.account_data_last_month;
export const account_data_this_month = () => currentDates.account_data_this_month;
export const account_data_two_months = () => currentDates.account_data_two_months;
export const account_data_previous_month_last_year = () => currentDates.account_data_previous_month_last_year;
export const account_data_this_month_last_year = () => currentDates.account_data_this_month_last_year;

export const account_purchase_events_today_hourly = () => currentDates.account_purchase_events_today_hourly;
export const account_purchase_events_yesterday = () => currentDates.account_purchase_events_yesterday;
export const account_purchase_events_last_7 = () => currentDates.account_purchase_events_last_7;

export const account_campaign_stats_today_hourly = () => currentDates.account_campaign_stats_today_hourly;
export const account_campaign_stats_yesterday = () => currentDates.account_campaign_stats_yesterday;
export const account_campaign_stats_last_7 = () => currentDates.account_campaign_stats_last_7;


export const fb_insights_previous_7 = () => currentDates.fb_insights_previous_7;
export const fb_insights_last_3 = () => currentDates.fb_insights_last_3;
export const fb_insights_last_7 = () => currentDates.fb_insights_last_7;
export const fb_insights_this_month = () => currentDates.fb_insights_this_month;
export const fb_insights_last_month = () => currentDates.fb_insights_last_month;
export const fb_insights_two_months = () => currentDates.fb_insights_two_months;
export const fb_insights_this_month_last_year = () => currentDates.fb_insights_this_month_last_year;
export const fb_insights_previous_month_last_year = () => currentDates.fb_insights_previous_month_last_year;
export const fb_adsets_today = () => currentDates.fb_adsets_today;
export const fb_adsets_today_hourly = () => currentDates.fb_adsets_today_hourly;
export const fb_adsets_yesterday = () => currentDates.fb_adsets_yesterday;
export const fb_adsets_last_7 = () => currentDates.fb_adsets_last_7;
export const fb_campaigns_last_7 = () => currentDates.fb_campaigns_last_7;
export const fb_campaigns_last_7_active = () => currentDates.fb_campaigns_last_7_active;
export const fb_campaigns_today = () => currentDates.fb_campaigns_today;
export const fb_campaigns_today_hourly = () => currentDates.fb_campaigns_today_hourly;
export const fb_campaigns_yesterday = () => currentDates.fb_campaigns_yesterday;
export const fb_campaign_list_hourly = () => currentDates.fb_campaign_list_hourly;